import React, { useState } from 'react';
import { User, Car, FileText, Monitor, X } from 'lucide-react';

import { useI18n } from '@/shared/i18n';
import { useToast, getErrorMessage } from '@/app/providers';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { Guest } from '@/shared/types';
import type { AvailableDevice, UpdateGuestDTO } from '@yawi/domain';
import { ApiError, toIsoWithOffset } from '@yawi/infrastructure';
import { GUEST_ERROR_CODES } from '@yawi/types';

interface GuestFormProps {
  hostId: string;
  onSuccess: (guest: Guest) => void;
  onCancel: () => void;
  createGuestFn?: (guest: any) => Promise<Guest>;
  updateGuestFn?: (guestId: string, data: UpdateGuestDTO) => Promise<Guest>;
  availableDevices?: AvailableDevice[];
  initialValues?: Guest;
}

type ValidityType = 'ONCE' | 'RANGE' | 'PERMANENT';

export const GuestForm: React.FC<GuestFormProps> = ({
  hostId,
  onSuccess,
  onCancel,
  createGuestFn,
  updateGuestFn,
  availableDevices = [],
  initialValues,
}) => {
  const isEdit = Boolean(initialValues);
  const { t } = useI18n();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  // Form State
  const [name, setName] = useState(initialValues?.name ?? '');
  const [type, setType] = useState<'VISIT' | 'SERVICE' | 'DELIVERY' | 'PARTY'>(
    (initialValues?.type as any) ?? 'VISIT'
  );
  const [validityType, setValidityType] = useState<ValidityType>('ONCE');
  const [startDate, setStartDate] = useState(
    initialValues?.validFrom ? initialValues.validFrom.split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    initialValues?.validTo ? initialValues.validTo.split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [startTime, setStartTime] = useState(
    initialValues?.validFrom ? new Date(initialValues.validFrom).toTimeString().slice(0, 5) : '08:00'
  );
  const [endTime, setEndTime] = useState(
    initialValues?.validTo ? new Date(initialValues.validTo).toTimeString().slice(0, 5) : '22:00'
  );
  const [plate, setPlate] = useState(initialValues?.plateNumber ?? '');
  const [notes, setNotes] = useState(initialValues?.notes ?? '');

  // Door selection: allDoors=true → send deviceIds:[] (any door); false → send selectedDeviceIds
  const initAllDoors = !initialValues?.allowedDeviceIds || initialValues.allowedDeviceIds.length === 0;
  const [allDoors, setAllDoors] = useState(initAllDoors);
  const [deviceIds, setDeviceIds] = useState<string[]>(
    initialValues?.allowedDeviceIds ?? []
  );

  const toggleDevice = (id: string) => {
    setDeviceIds(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const now = new Date();
      let validFrom = toIsoWithOffset(now);
      let validTo = toIsoWithOffset(now);

      if (validityType === 'ONCE') {
        validFrom = toIsoWithOffset(now);
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        validTo = toIsoWithOffset(tomorrow);
      } else if (validityType === 'PERMANENT') {
        validFrom = toIsoWithOffset(now);
        const future = new Date(now);
        future.setFullYear(future.getFullYear() + 100);
        validTo = toIsoWithOffset(future);
      } else if (validityType === 'RANGE') {
        validFrom = toIsoWithOffset(new Date(`${startDate}T${startTime}:00`));
        validTo = toIsoWithOffset(new Date(`${endDate}T${endTime}:00`));
      }

      const resolvedDeviceIds = allDoors ? [] : deviceIds;

      if (isEdit && updateGuestFn && initialValues) {
        const updated = await updateGuestFn(initialValues.id, {
          name,
          type,
          validFrom,
          validTo,
          plateNumber: plate.toUpperCase() || undefined,
          notes: notes || undefined,
          deviceIds: resolvedDeviceIds,
        });
        onSuccess(updated);
      } else if (createGuestFn) {
        const created = await createGuestFn({
          hostId,
          name,
          type,
          validFrom,
          validTo,
          plateNumber: plate.toUpperCase(),
          notes,
          deviceIds: resolvedDeviceIds,
        });
        onSuccess(created);
      }
    } catch (error) {
      console.error(error);
      if (ApiError.isApiError(error) && error.code === GUEST_ERROR_CODES.ACCESS_LEVEL_NOT_CONFIGURED) {
        toast.error(
          'La administración aún no configuró los permisos de visitantes. Pide ayuda al administrador.',
          'No se puede crear la invitación',
        );
        return;
      }
      toast.error(getErrorMessage(error), isEdit ? 'Error al actualizar invitación' : 'Error al crear invitación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-slate-800">
          {isEdit ? 'Editar invitación' : t('guest.form.title')}
        </h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Name */}
        <Input
          label={t('guest.form.name')}
          placeholder={t('guest.form.name_ph')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          icon={<User size={16}/>}
        />

        {/* Type & Validity Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('guest.form.type')}</label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
              value={type}
              onChange={(e) => setType(e.target.value as any)}
            >
              <option value="VISIT">{t('guest.type.visit')}</option>
              <option value="SERVICE">{t('guest.type.service')}</option>
              <option value="DELIVERY">{t('guest.type.delivery')}</option>
              <option value="PARTY">{t('guest.type.party')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('guest.form.validity')}</label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
              value={validityType}
              onChange={(e) => setValidityType(e.target.value as any)}
            >
              <option value="ONCE">{t('guest.validity.once')}</option>
              <option value="RANGE">{t('guest.validity.range')}</option>
              <option value="PERMANENT">{t('guest.validity.permanent')}</option>
            </select>
          </div>
        </div>

        {/* Date Range Inputs (Conditional) */}
        {validityType === 'RANGE' && (
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1 block">Desde</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full text-sm border-slate-300 rounded mb-2"/>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full text-sm border-slate-300 rounded"/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1 block">Hasta</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full text-sm border-slate-300 rounded mb-2"/>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full text-sm border-slate-300 rounded"/>
            </div>
          </div>
        )}

        {/* Plate & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('guest.form.plate')}
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            placeholder="ABC-123"
            icon={<Car size={16}/>}
          />
          <Input
            label={t('guest.form.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="..."
            icon={<FileText size={16}/>}
          />
        </div>

        {/* Device Selector */}
        {availableDevices.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
              <Monitor size={15} />
              Dispositivos autorizados
            </label>

            {/* All-doors toggle */}
            <button
              type="button"
              onClick={() => { setAllDoors(prev => !prev); if (!allDoors) setDeviceIds([]); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all mb-2 ${
                allDoors
                  ? 'bg-primary-50 border-primary-400 text-primary-700 font-medium'
                  : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${allDoors ? 'bg-primary-500' : 'bg-slate-300'}`} />
              Todas las puertas
            </button>

            {/* Specific device chips (visible only when allDoors is OFF) */}
            {!allDoors && (
              <div className="flex flex-wrap gap-2">
                {availableDevices.map(device => (
                  <button
                    key={device.id}
                    type="button"
                    onClick={() => toggleDevice(device.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all ${
                      deviceIds.includes(device.id)
                        ? 'bg-primary-50 border-primary-400 text-primary-700 font-medium'
                        : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${deviceIds.includes(device.id) ? 'bg-primary-500' : 'bg-slate-300'}`} />
                    {device.name}
                  </button>
                ))}
              </div>
            )}

            <p className="text-xs text-slate-400 mt-1">
              {allDoors
                ? 'El invitado podrá abrir cualquier acceso del condominio.'
                : 'Solo los dispositivos seleccionados validarán la credencial.'}
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={onCancel}>
            {t('guest.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" className="flex-1" isLoading={loading}>
            {isEdit ? 'Guardar cambios' : t('guest.form.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};
